import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, Mic, Search, Square } from 'lucide-react';
import { api } from '@lib/api';
import { useCreateUrun } from '@features/urun';
import type { CreateUrunInput } from '@/types/urun';

/**
 * Zo — sayfa-farkındalıklı asistan.
 * Yanında sabit "? Bana sor" rozeti durur; tıklanınca sayfaya özel ipucu ve
 * araçlarla panel açılır:
 *  - Ürünler: sesle ürün ekleme (Web Speech tr-TR → alan ayıklama → önizle → kaydet)
 *  - Diğer sayfalar: yazılan kelimeyi global aramada (Ctrl+K paleti) bulur
 */

interface Props {
  /** Zo'nun aramasını global palete taşır (AppLayout bağlar). */
  onSearch: (query: string) => void;
}

/* ---------- rota → ipucu ---------- */
const CONTEXTS: Array<{
  match: (path: string) => boolean;
  tip: string;
  placeholder: string;
  voiceUrun?: boolean;
}> = [
  {
    match: (p) => p.startsWith('/urunler'),
    tip: "Hangi ürünü eklemek istiyorsun? Mikrofona söyle — adını, fiyatını, KDV'sini ben işlerim.",
    placeholder: 'Ürün ara…',
    voiceUrun: true,
  },
  {
    match: (p) => p.startsWith('/takvim') || p.startsWith('/aktiviteler'),
    tip: 'Takvimde bir aktiviteyi mi arıyorsun? Kelimeyi yaz, ben bulayım.',
    placeholder: 'Örn: numune sunumu…',
  },
  {
    match: (p) => p.startsWith('/musteriler'),
    tip: 'Aradığın müşteriyi yaz, bulayım. Yeni müşteriyi üstteki "Sesle Ekle" ile söyleyebilirsin.',
    placeholder: 'Müşteri adı / firma…',
  },
  {
    match: (p) => p.startsWith('/firsatlar'),
    tip: 'Hangi fırsatı arıyorsun? Yaz, panoda bulayım.',
    placeholder: 'Fırsat / firma adı…',
  },
  {
    match: (p) => p.startsWith('/teklifler') || p.startsWith('/siparisler'),
    tip: 'Teklif ya da sipariş numarasını / müşteriyi yaz, getireyim.',
    placeholder: 'Örn: TEK-2026-0042…',
  },
  {
    match: () => true,
    tip: 'Ne arıyorsan yaz — müşteri, fırsat, teklif… tek aramada bulurum.',
    placeholder: 'Ara…',
  },
];

/* ---------- sesli ürün metni → alanlar (istemci tarafı ayıklama) ---------- */
export function parseUrunFromSpeech(raw: string): CreateUrunInput {
  const metin = raw.replace(/\s+/g, ' ').trim();
  let kalan = metin;

  // KDV: "kdv yüzde 20", "kdv'si %20", "yüzde 20 kdv"
  let kdvOrani: number | undefined;
  const kdv =
    kalan.match(/kdv['’]?\w*\s*(?:yüzde|%)?\s*(\d{1,2})/i) ??
    kalan.match(/(?:yüzde|%)\s*(\d{1,2})\s*kdv/i);
  if (kdv?.[0] && kdv[1]) {
    kdvOrani = Number(kdv[1]);
    kalan = kalan.replace(kdv[0], ' ');
  }

  // Fiyat: "fiyatı 1250 lira", "2.500 TL", "birim fiyat 99,90 ₺"
  let birimFiyat = 0;
  const fiyat = kalan.match(
    /(?:birim\s*fiyat[ıi]?|fiyat[ıi]?)?\s*([\d.]+(?:,\d{1,2})?)\s*(tl|lira|₺)/i,
  );
  if (fiyat?.[0] && fiyat[1]) {
    birimFiyat = Number(fiyat[1].replace(/\./g, '').replace(',', '.'));
    kalan = kalan.replace(fiyat[0], ' ');
  }

  // Birim: adet/kilo/metre/saat/litre/paket/kutu
  let birim: string | undefined;
  const birimEs = kalan.match(/\b(adet|kilo(?:gram)?|kg|metre|saat|litre|paket|kutu)\b/i);
  if (birimEs?.[0] && birimEs[1]) {
    const b = birimEs[1].toLowerCase();
    birim = b === 'kilo' || b === 'kilogram' ? 'kg' : b;
    kalan = kalan.replace(birimEs[0], ' ');
  }

  // Tip: "hizmet" geçiyorsa HIZMET
  const tipi = /\bhizmet\b/i.test(metin) ? ('HIZMET' as const) : undefined;

  // Ad: kalan metinden dolgu sözcükleri ayıkla
  const ad = kalan
    .replace(
      /\b(ürün(?:ün)?\s*ad[ıi]|ürün|ekle(?:mek)?(?:\s*istiyorum)?|yeni|bir|lütfen|adı|ismi|fiyat[ıi]?|birim|kdv['’]?\w*)\b/gi,
      ' ',
    )
    .replace(/[.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    ad: ad || metin.split(' ').slice(0, 5).join(' '),
    birimFiyat,
    ...(kdvOrani !== undefined ? { kdvOrani } : {}),
    ...(birim ? { birim } : {}),
    ...(tipi ? { tipi } : {}),
  };
}

/* ---------- Web Speech sarmalayıcı (SesKayitDialog kalıbı) ---------- */
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
};

function createRecognition(): SpeechRecognitionLike | null {
  const Ctor =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!Ctor) return null;
  const rec: SpeechRecognitionLike = new Ctor();
  rec.lang = 'tr-TR';
  rec.continuous = true;
  rec.interimResults = true;
  return rec;
}

export function ZoMaskot({ onSearch }: Props) {
  const location = useLocation();
  const ctx = useMemo(
    () => CONTEXTS.find((c) => c.match(location.pathname))!,
    [location.pathname],
  );

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  // Sayfa değişince açık paneli kapat
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // ESC + dışarı tıklama ile kapat
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  /* ---- sesli ürün ekleme durumu ---- */
  const [dinliyor, setDinliyor] = useState(false);
  const [aiDusunuyor, setAiDusunuyor] = useState(false);
  const [metin, setMetin] = useState('');
  const [taslak, setTaslak] = useState<CreateUrunInput | null>(null);
  const [sesHata, setSesHata] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const metinRef = useRef('');
  const createUrun = useCreateUrun();

  const sesBaslat = () => {
    setSesHata(null);
    setTaslak(null);
    const rec = createRecognition();
    if (!rec) {
      setSesHata('Bu tarayıcı ses tanımayı desteklemiyor (Chrome/Edge kullanın).');
      return;
    }
    let kalici = '';
    rec.onresult = (e: any) => {
      let gecici = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const parca = e.results[i][0].transcript;
        if (e.results[i].isFinal) kalici += parca + ' ';
        else gecici += parca;
      }
      const m = (kalici + gecici).replace(/\s+/g, ' ').trimStart();
      metinRef.current = m;
      setMetin(m);
    };
    rec.onerror = (e: any) => {
      setSesHata(
        e?.error === 'not-allowed'
          ? 'Mikrofon izni reddedildi.'
          : 'Ses tanıma hatası: ' + (e?.error ?? 'bilinmiyor'),
      );
      setDinliyor(false);
    };
    rec.onend = () => setDinliyor(false);
    recRef.current = rec;
    metinRef.current = '';
    setMetin('');
    rec.start();
    setDinliyor(true);
  };

  const sesDurdur = () => {
    recRef.current?.stop();
    setDinliyor(false);
    const konusma = metinRef.current.trim();
    if (!konusma) return;

    const regexTaslak = parseUrunFromSpeech(konusma);
    // Regex temiz sonuç verdiyse LLM'e gerek yok (maliyet ₺0).
    if (regexTaslak.ad && regexTaslak.birimFiyat > 0) {
      setTaslak(regexTaslak);
      return;
    }
    // Regex eksik kaldı (dolgu sesleri, düzeltmeler, karışık sıra) →
    // backend Haiku ayıklamasına düş; o da olmazsa regex taslağıyla devam.
    setAiDusunuyor(true);
    api
      .post('ai/urun-ayikla', { json: { metin: konusma } })
      .json<{ data: CreateUrunInput }>()
      .then((r) => setTaslak(r.data))
      .catch(() => {
        setTaslak(regexTaslak);
        setSesHata('Zo alanların hepsini çıkaramadı — taslağı elle tamamlayın.');
      })
      .finally(() => setAiDusunuyor(false));
  };

  const taslakKaydet = async () => {
    if (!taslak || !taslak.ad || !(taslak.birimFiyat > 0)) {
      setSesHata("Ürün adı ve 0'dan büyük fiyat gerekli — taslağı düzeltin.");
      return;
    }
    try {
      await createUrun.mutateAsync(taslak);
      setTaslak(null);
      setMetin('');
      setSesHata(null);
      setOpen(false);
    } catch (err) {
      setSesHata(err instanceof Error ? err.message : 'Ürün eklenemedi');
    }
  };

  const searchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    onSearch(q.trim());
    setQ('');
  };

  // Mesajlaşma ekranında (Meta Yönetim, açık konuşma) sağ alttaki rozet
  // "Gönder" düğmesinin ve müşteri panelinin üstüne biniyor — orada gizlenir.
  if (location.pathname.startsWith('/meta-yonetim') && new URLSearchParams(location.search).has('k')) {
    return null;
  }

  return (
    <div
      ref={boxRef}
      className="fixed bottom-20 right-3 z-40 flex flex-col items-end gap-2 md:bottom-5 md:right-5"
    >
      {/* Sabit rozet — panel kapalıyken hep görünür */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-xs font-medium shadow-md transition-transform hover:scale-105"
        >
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
            ?
          </span>
          Bana sor
        </button>
      )}

      {open && (
        <div className="w-80 rounded-xl border bg-background p-4 shadow-xl">
          <div className="flex items-center gap-2.5">
            <img src="/zo/zed.png" alt="" className="h-9 w-auto" draggable={false} />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Zo — asistanın</div>
              <div className="text-xs leading-snug text-muted-foreground">{ctx.tip}</div>
            </div>
          </div>

          {/* Arama — her sayfada */}
          <form onSubmit={searchSubmit} className="mt-3 flex gap-1.5">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={ctx.placeholder}
              className="h-9 flex-1 rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="submit"
              className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:opacity-90"
              aria-label="Ara"
            >
              <Search className="h-4 w-4" />
            </button>
          </form>

          {/* Sesle ürün ekleme — yalnız Ürünler sayfasında */}
          {ctx.voiceUrun && (
            <div className="mt-3 border-t pt-3">
              {aiDusunuyor && (
                <div className="flex items-center justify-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Zo alanları çıkarıyor…
                </div>
              )}

              {!dinliyor && !taslak && !aiDusunuyor && (
                <button
                  type="button"
                  onClick={sesBaslat}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  <Mic className="h-4 w-4" /> Sesle ürün ekle
                </button>
              )}

              {dinliyor && (
                <div className="space-y-2">
                  <div className="rounded-md border bg-muted/50 p-2 text-xs leading-relaxed">
                    {metin ||
                      'Dinliyorum… Örn: "Paletleme robotu, fiyatı 2500 lira, KDV yüzde 20, adet"'}
                  </div>
                  <button
                    type="button"
                    onClick={sesDurdur}
                    className="flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
                  >
                    <Square className="h-3.5 w-3.5" /> Bitti — alanları çıkar
                  </button>
                </div>
              )}

              {taslak && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground">
                    Kontrol et, gerekirse düzelt:
                  </div>
                  <input
                    value={taslak.ad}
                    onChange={(e) => setTaslak({ ...taslak, ad: e.target.value })}
                    placeholder="Ürün adı"
                    className="h-9 w-full rounded-md border px-3 text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={taslak.birimFiyat || ''}
                      onChange={(e) =>
                        setTaslak({ ...taslak, birimFiyat: Number(e.target.value) })
                      }
                      placeholder="Fiyat (₺)"
                      className="h-9 w-full rounded-md border px-3 text-sm"
                    />
                    <input
                      type="number"
                      value={taslak.kdvOrani ?? ''}
                      onChange={(e) =>
                        setTaslak({ ...taslak, kdvOrani: Number(e.target.value) })
                      }
                      placeholder="KDV %"
                      className="h-9 w-24 rounded-md border px-3 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={taslakKaydet}
                      disabled={createUrun.isPending}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
                    >
                      {createUrun.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Ürünü ekle
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTaslak(null);
                        setMetin('');
                      }}
                      className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
                    >
                      Vazgeç
                    </button>
                  </div>
                </div>
              )}

              {sesHata && <p className="mt-2 text-xs text-destructive">{sesHata}</p>}
            </div>
          )}

          <p className="mt-3 border-t pt-2 text-[11px] text-muted-foreground">
            Destek:{' '}
            <a
              href="https://wa.me/905437446778"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              WhatsApp
            </a>{' '}
            ·{' '}
            <a href="mailto:hello@zoprey.com" className="underline hover:text-foreground">
              e-posta
            </a>{' '}
            ·{' '}
            <Link
              to="/egitim"
              onClick={() => setOpen(false)}
              className="underline hover:text-foreground"
            >
              eğitim
            </Link>
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Zo asistanı kapat' : 'Zo asistanı aç'}
        aria-expanded={open}
        className="zo-maskot-btn rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <img
          src="/zo/zed.png"
          alt=""
          className="h-14 w-auto drop-shadow-lg md:h-16"
          draggable={false}
        />
      </button>
    </div>
  );
}
